import { useMemo } from 'react'
import type { ActionItem, IActionsProps } from 'shared/ui'
import { uiStore } from 'shared/store/uiStore'
import type { KnowledgeBaseDocument } from 'entities/KnowledgeBase/model/KnowledgeBaseDocument'
import { KnowledgeBasePath } from 'entities/ExportRouters'
import { useKnowledgeBaseViewContext } from 'pages/settings/pages/knowledgeBase/context/knowledgeBaseViewContext'
import { UNSUPPORTED_FORMATS } from 'pages/settings/pages/knowledgeBase/lib/constants'

type IDocumentWithActionsRow = KnowledgeBaseDocument & {
  actionsProps: IActionsProps
  isCheckboxDisabled: boolean
}

type IUseDocumentsWithActionsRows = () => IDocumentWithActionsRow[]

export const useDocumentsWithActionsRows: IUseDocumentsWithActionsRows = () => {
  const {
    documentsStore: {
      documents,
      onDeleteDocuments,
      onDownloadDocument,
      onRetryIndexation,
      onPreview,
      onReconnectGoogleDocument,
      urlStore,
      createDocumentStore,
    },
  } = useKnowledgeBaseViewContext()

  return useMemo(() => {
    return documents.map((item) => {
      const isActionsNotAvailable = item.getIsUploading() || item.getIsIndexing()
      const isGoogleLive = item.getIsLiveDocument()

      const makeDownloadAction: ActionItem = {
        icon: 'download',
        text: 'Download',
        onAction: () => onDownloadDocument(item.id),
      }

      const makeDeleteAction: ActionItem = {
        icon: 'delete',
        text: 'Delete',
        onAction: () => onDeleteDocuments([item.id]),
      }

      const tryAgainAction: ActionItem = {
        icon: 'refresh',
        text: 'Try again',
        onAction: () => onRetryIndexation([item.id]),
      }

      const makePreviewAction: ActionItem = {
        icon: 'eye',
        text: 'Preview',
        onAction: () => onPreview(item),
      }

      const makeEditAction: ActionItem = {
        icon: 'edit',
        text: 'Edit',
        onAction: async () => {
          const result = await createDocumentStore.onEditDocument(item.id)
          if (result?.isLocked) {
            return
          }

          uiStore.changeRouteViaVue({
            path: `${KnowledgeBasePath.editDocument}/${item.id}`,
          })
        },
      }

      const makeResyncAction: ActionItem = {
        icon: 'refresh',
        text: 'Re-sync',
        onAction: () => urlStore.handleResyncUrl(item.id),
      }

      const makeOpenExternalAction: ActionItem = {
        icon: 'linkExternalLine',
        text: 'Open in new tab',
        onAction: () => onPreview(item),
      }

      const makeReconnectAction: ActionItem = {
        icon: 'refresh',
        text: 'Reconnect',
        onAction: () => onReconnectGoogleDocument(item),
      }

      if (isGoogleLive) {
        const googleActions: ActionItem[] = isActionsNotAvailable
          ? [makeDeleteAction]
          : [
              ...(item.getIsAccessLost() ? [makeReconnectAction] : []),
              makeOpenExternalAction,
              makeDeleteAction,
            ]

        return {
          ...item,
          actionsProps: { actions: googleActions },
          isCheckboxDisabled: isActionsNotAvailable,
        }
      }

      const documentActions = [
        ...(item.getIsIndexationFailed() ? [tryAgainAction] : []),
        ...(UNSUPPORTED_FORMATS.includes(item.contentType) ? [] : [makePreviewAction]),
        ...(item.source === 'created' ? [makeEditAction] : []),
        ...(item.source === 'web_url' ? [makeResyncAction] : []),
        makeDownloadAction,
        makeDeleteAction,
      ]

      return {
        ...item,
        actionsProps: {
          actions: isActionsNotAvailable ? [] : documentActions,
        },
        isCheckboxDisabled: isActionsNotAvailable,
      }
    })
  }, [documents])
}
